import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { getMemberDetail, addDependent, deleteDependent, deleteMemberFollowup, getTezkar, addTezkar, deleteTezkar, reorderTezkar, updateDependentLifeStatus, getDependentStatusHistory, getChurchProfile, promoteDependent, getFundCategories, subscribeToType, setPaysIndependently, unsubscribeFromType } from '../../services/api';
import { FiArrowLeft, FiUser, FiUsers, FiHeart, FiDollarSign, FiActivity, FiClock, FiPlus, FiTrash2, FiEdit2, FiBookOpen, FiX, FiPrinter, FiUserPlus, FiArrowUpRight, FiZap } from 'react-icons/fi';
import { openPreview } from '../../utils/printHtml';
import DependentCard from '../../components/DependentCard';
import AdminLencoChargeModal from '../../components/AdminLencoChargeModal';
import useAppRefresh from '../../hooks/useAppRefresh';
import Toast from '../../components/Toast';
import PhoneLink from '../../components/PhoneLink';
import PhoneInput from '../../components/PhoneInput';
import { useAuth } from '../../context/AuthContext';

const currentYear = new Date().getFullYear();

const statusPill = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red' };
const statusLabel = { member: 'Active Member', inactive: 'Inactive', transferred: 'Transferred', changed_religion: 'Changed Religion', deceased: 'Deceased', suspended: 'Suspended' };
const relColors = { wife: 'pill-purple', husband: 'pill-blue', son: 'pill-blue', daughter: 'pill-purple', other: 'pill-gray' };

// Dependent statuses now mirror member statuses (+ legacy 'active').
const DEP_STATUS_OPTIONS = [
  { value: 'member',           label: 'Active Member' },
  { value: 'inactive',         label: 'Inactive' },
  { value: 'transferred',      label: 'Transferred' },
  { value: 'changed_religion', label: 'Changed Religion' },
  { value: 'deceased',         label: 'Deceased' },
  { value: 'suspended',        label: 'Suspended' },
  { value: 'other',            label: 'Other' },
];
const DEP_STATUS_LABEL = { member: 'Active Member', inactive: 'Inactive', transferred: 'Transferred', changed_religion: 'Changed Religion', deceased: 'Deceased', suspended: 'Suspended', other: 'Other', active: 'Active' };
const DEP_STATUS_PILL = { member: 'pill-green', inactive: 'pill-red', transferred: 'pill-blue', changed_religion: 'pill-gray', deceased: 'pill-gray', suspended: 'pill-red', other: 'pill-gray', active: 'pill-green' };
const DEP_REASONS = {
  transferred:      ['Moved to another city', 'Moved to another country', 'Joined another church', 'Other'],
  changed_religion: ['Personal decision', 'Marriage', 'Other'],
  deceased:         ['Natural causes', 'Illness', 'Accident', 'Other'],
  suspended:        ['Disciplinary action', 'Own request', 'Other'],
  inactive:         ['Stopped attending', 'Personal reasons', 'Health issues', 'Other'],
  member:           ['Rejoined', 'Re-activated', 'Other'],
  other:            ['Other'],
};

const getTabs = (t) => [
  { key: 'overview',       label: t('members.profile'),     icon: <FiUser /> },
  { key: 'dependents',     label: t('members.dependents'),  icon: <FiUsers /> },
  { key: 'tezkar',         label: t('members.tezkar'),      icon: <FiBookOpen /> },
  { key: 'followups',      label: t('followup.title'),      icon: <FiHeart /> },
  { key: 'contributions',  label: t('subscriptions.title'), icon: <FiDollarSign /> },
  { key: 'attendance',     label: t('nav.attendance'),      icon: <FiActivity /> },
  { key: 'status_history', label: t('common.status'),       icon: <FiClock /> },
];

const Field = ({ label, value }) => value ? (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{value}</div>
  </div>
) : null;

const MemberProfile = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add dependent form
  const [addingDep, setAddingDep] = useState(false);
  const [newDep, setNewDep] = useState({ first_name: '', middle_name: '', last_name: '', relationship: 'wife', birth_year: '', holy_communion: 'unknown', country: 'Zambia', phone: '', whatsapp: '' });

  // Tezkar
  const [tezkarList, setTezkarList] = useState([]);
  const [newTezkarName, setNewTezkarName] = useState('');
  const [addingTezkar, setAddingTezkar] = useState(false);
  const [tezkarSort, setTezkarSort] = useState('custom'); // custom | az | newest | oldest

  // Dependent status change
  const [depStatusModal, setDepStatusModal] = useState(null);
  const [depStatusForm, setDepStatusForm] = useState({ life_status: '', reason: '', custom_reason: '', notes: '' });
  const [depHistory, setDepHistory] = useState([]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const [churchProfile, setChurchProfile] = useState({});
  // Admin "Assign Subscription" modal — used to back-date a subscription so
  // historical arrears show as overdue from the moment the member is imported.
  const [funds, setFunds] = useState([]);
  const [assignSub, setAssignSub] = useState(null);
  const [showLencoCharge, setShowLencoCharge] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);

  const load = () => getMemberDetail(id).then(r => setMember(r.data || null)).catch(() => {});
  useAppRefresh(load);
  const loadTezkar = () => getTezkar(id).then(r => setTezkarList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
    loadTezkar();
    getChurchProfile().then(r => setChurchProfile(r.data || {})).catch(() => {});
    getFundCategories().then(r => setFunds(Array.isArray(r.data) ? r.data.filter(f => f.frequency) : [])).catch(() => {});
  }, [id]);

  const handleAddDep = async () => {
    if (!newDep.first_name) return;
    setSaving(true);
    try {
      await addDependent(id, { ...newDep, country: newDep.country || 'Zambia', phone: newDep.phone || null, whatsapp: newDep.whatsapp || null });
      setAddingDep(false);
      setNewDep({ first_name: '', middle_name: '', last_name: '', relationship: 'wife', birth_year: '', holy_communion: 'unknown', country: 'Zambia' });
      showToast('Dependent added');
      load();
    } catch (e) { showToast('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteDep = async (depId) => {
    if (!window.confirm('Remove this dependent?')) return;
    await deleteDependent(depId);
    showToast('Removed', 'error');
    load();
  };

  const handlePromoteDep = async (d) => {
    const fullName = [d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' ') || d.name;
    if (!window.confirm(`Promote ${fullName} to a sub-member? They will get their own card number, QR, and can be linked to a user account for login.`)) return;
    try {
      const r = await promoteDependent(id, d.id);
      showToast(`Promoted — card ${r.data.card_number}`);
      load();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to promote', 'error'); }
  };

  const openDepStatusModal = (d) => {
    setDepStatusModal(d);
    // Default the dropdown to the dependent's current status.
    setDepStatusForm({ life_status: d.life_status || 'member', reason: '', custom_reason: '', notes: '' });
    setDepHistory([]);
    getDependentStatusHistory(d.id).then(r => setDepHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  const handleSaveDepStatus = async () => {
    if (!depStatusForm.life_status) return showToast('Select a new status', 'error');
    setSaving(true);
    try {
      await updateDependentLifeStatus(depStatusModal.id, depStatusForm);
      showToast('Dependent status updated');
      setDepStatusModal(null);
      load();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const openAssignSub = () => {
    setAssignSub({ fund_category_id: '', amounts: {}, start_date: todayIso, saving: false });
  };
  const handleSubmitAssignSub = async () => {
    if (!assignSub.fund_category_id) return showToast('Pick a contribution type', 'error');
    const clean = Object.fromEntries(
      Object.entries(assignSub.amounts || {})
        .filter(([, v]) => parseFloat(v) > 0)
        .map(([k, v]) => [k, parseFloat(v)]),
    );
    if (Object.keys(clean).length === 0) return showToast('Enter at least one pledge amount', 'error');
    setAssignSub(p => ({ ...p, saving: true }));
    try {
      const r = await subscribeToType({
        fund_category_id: assignSub.fund_category_id,
        member_id: id,
        pledges: clean,
        start_date: assignSub.start_date || todayIso,
      });
      const back = r.data?.generated || 0;
      showToast(back > 1 ? `Subscribed. Back-filled ${back} periods.` : 'Subscribed');
      if (r.data?.capped) showToast('Capped at 200 periods — check the start date.', 'error');
      setAssignSub(null);
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to subscribe', 'error');
      setAssignSub(p => ({ ...p, saving: false }));
    }
  };

  const handleDeleteFup = async (fupId) => {
    if (!window.confirm('Delete this follow-up?')) return;
    await deleteMemberFollowup(fupId);
    showToast('Deleted', 'error');
    load();
  };

  const setD = f => e => setNewDep(p => ({ ...p, [f]: e.target.value }));

  const handlePrintProfile = () => {
    if (!member) return;
    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const yr = new Date().getFullYear();
    const fullNm = [member.title, member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ');
    const row = (label, val) => val ? `<tr><td class="lbl">${esc(label)}</td><td>${esc(val)}</td></tr>` : '';
    const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

    const personal = `
      <table class="kv">
        ${row('Card No.', member.card_number)}
        ${row('Full Name', fullNm)}
        ${row('Christian Name', member.christian_name)}
        ${row('Gender', member.gender)}
        ${row('Year of Birth', member.birth_year ? `${member.birth_year} (age ${yr - parseInt(member.birth_year)})` : '')}
        ${row('Marital Status', member.marital_status)}
        ${row('Occupation', member.occupation)}
        ${row('Phone', member.phone)}
        ${row('Email', member.email)}
        ${row('Address', member.address)}
        ${row('Holy Communion', member.holy_communion)}
        ${row('Membership Status', DEP_STATUS_LABEL[member.membership_status] || member.membership_status)}
        ${row('Department', member.department)}
        ${row('Zone', member.zone)}
        ${row('Spiritual Father', member.spiritual_father_name)}
        ${row('Joined', member.join_date)}
        ${row('Notes', member.notes)}
      </table>`;

    const deps = (member.dependents || []);
    const depsHtml = deps.length ? `
      <table class="grid"><thead><tr><th>Name</th><th>Relationship</th><th>Birth Yr</th><th>Holy Communion</th><th>Status</th></tr></thead><tbody>
      ${deps.map(d => `<tr><td>${esc([d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' ') || d.name)}</td><td>${esc(d.relationship)}</td><td>${esc(d.birth_year || '')}</td><td>${esc(d.holy_communion || '')}</td><td>${esc(DEP_STATUS_LABEL[d.life_status] || d.life_status || 'Active')}</td></tr>`).join('')}
      </tbody></table>` : '<p class="muted">No dependents.</p>';

    const cons = (member.contributions || []);
    const totUsd = cons.reduce((s, c) => s + parseFloat(c.amount_usd || 0), 0);
    const totKw = cons.reduce((s, c) => s + parseFloat(c.amount_kwacha || 0), 0);
    const consHtml = cons.length ? `
      <table class="grid"><thead><tr><th>Date</th><th>CR #</th><th>Fund</th><th>USD</th><th>Kwacha</th><th>Method</th></tr></thead><tbody>
      ${cons.map(c => `<tr><td>${esc(c.date)}</td><td>${esc(c.cr_number)}</td><td>${esc(c.fund_name || '')}</td><td>${money(c.amount_usd)}</td><td>${money(c.amount_kwacha)}</td><td>${esc(c.payment_method || '')}</td></tr>`).join('')}
      <tr class="tot"><td colspan="3">Total</td><td>${money(totUsd)}</td><td>${money(totKw)}</td><td></td></tr>
      </tbody></table>` : '<p class="muted">No contributions recorded.</p>';

    const att = (member.attendance || []);
    const attHtml = att.length ? `
      <table class="grid"><thead><tr><th>Date</th><th>Service</th><th>Type</th></tr></thead><tbody>
      ${att.slice(0, 50).map(a => `<tr><td>${esc(a.date)}</td><td>${esc(a.service_name || '')}</td><td>${esc(a.type || '')}</td></tr>`).join('')}
      </tbody></table>${att.length > 50 ? `<p class="muted">Showing latest 50 of ${att.length}.</p>` : ''}` : '<p class="muted">No attendance records.</p>';

    const fups = (member.followups || []);
    const fupsHtml = fups.length ? `
      <table class="grid"><thead><tr><th>Date</th><th>Status</th><th>Notes</th><th>By</th></tr></thead><tbody>
      ${fups.map(f => `<tr><td>${esc(f.date)}</td><td>${esc((f.status || '').replace('_', ' '))}</td><td>${esc(f.notes || '')}</td><td>${esc(f.done_by_name || '')}</td></tr>`).join('')}
      </tbody></table>` : '<p class="muted">No follow-ups.</p>';

    const hist = (member.statusHistory || []);
    const histHtml = hist.length ? `
      <table class="grid"><thead><tr><th>Date</th><th>From</th><th>To</th><th>Reason</th><th>By</th></tr></thead><tbody>
      ${hist.map(h => `<tr><td>${esc((h.changed_at || '').slice(0, 10))}</td><td>${esc(h.old_status || '')}</td><td>${esc(h.new_status)}</td><td>${esc(h.custom_reason || h.reason || '')}</td><td>${esc(h.changed_by_name || '')}</td></tr>`).join('')}
      </tbody></table>` : '<p class="muted">No status changes.</p>';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(fullNm)} — Member Profile</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:24px;font-size:12px}
        h1{font-size:20px;margin:0}
        h2{font-size:14px;margin:22px 0 8px;border-bottom:2px solid #4f46e5;padding-bottom:4px;color:#4f46e5}
        .head{display:flex;align-items:center;gap:14px;border-bottom:1px solid #ddd;padding-bottom:12px}
        .sub{color:#666;font-size:12px}
        table{width:100%;border-collapse:collapse;margin-top:4px}
        table.kv td{padding:4px 8px;vertical-align:top}
        table.kv td.lbl{font-weight:700;color:#555;width:170px}
        table.grid th{background:#f3f4f6;text-align:left;padding:6px 8px;font-size:11px;border:1px solid #e5e7eb}
        table.grid td{padding:6px 8px;border:1px solid #e5e7eb}
        table.grid tr.tot td{font-weight:700;background:#f9fafb}
        .muted{color:#999;font-style:italic}
        @media print{body{margin:12px}}
      </style></head><body>
      <div class="head">
        <div><h1>${esc(churchProfile.name || 'Church')}</h1><div class="sub">Member Profile · printed ${new Date().toLocaleString()}</div></div>
      </div>
      <h2>Personal Information</h2>${personal}
      <h2>Dependents (${deps.length})</h2>${depsHtml}
      <h2>Contributions (${cons.length})</h2>${consHtml}
      <h2>Attendance (${att.length})</h2>${attHtml}
      <h2>Follow-ups (${fups.length})</h2>${fupsHtml}
      <h2>Status History (${hist.length})</h2>${histHtml}
      </body></html>`;
    openPreview(html);
  };

  if (!member) return <div className="page-content" style={{ color: '#6b7280' }}>Loading...</div>;

  const canManage = user?.role === 'admin' || (user?.member_id && member.spiritual_father_id === user.member_id);
  // Sub-members get a slimmer profile: no Dependents tab (no sub-roster) and
  // no My Contributions tab (financial history belongs to the head's profile).
  const isSubMember = !!member.head_member_id;
  const age = member.birth_year ? currentYear - parseInt(member.birth_year) : null;
  const fullName = [member.title, member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ');
  const totalContributions = (member.contributions || []).reduce((s, c) => s + parseFloat(c.amount), 0);
  // v1.9.46 — Split contributions by currency so $ and K aren't lumped into a
  // single meaningless number. Reads amount_usd / amount_kwacha off each row.
  const totalsByCurrency = (member.contributions || []).reduce((acc, c) => {
    const usd = parseFloat(c.amount_usd || 0) || 0;
    const kwa = parseFloat(c.amount_kwacha || 0) || 0;
    if (usd > 0) acc['$'] = (acc['$'] || 0) + usd;
    if (kwa > 0) acc['K'] = (acc['K'] || 0) + kwa;
    // Fallback: if neither currency column has data, count the raw amount
    // as-is into a generic bucket so old rows still show something.
    if (!usd && !kwa) acc[''] = (acc[''] || 0) + parseFloat(c.amount || 0);
    return acc;
  }, {});
  const fmtMoney = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/members')} style={{ flexShrink: 0 }}>
          <FiArrowLeft /> {t('common.back')}
        </button>
        <button className="btn btn-primary" onClick={() => navigate(`/members/${id}/edit`)} style={{ flexShrink: 0 }}>
          <FiEdit2 /> {t('common.edit')}
        </button>
        {user?.role === 'admin' && !isMobile && (
          <button className="btn btn-secondary" onClick={handlePrintProfile} style={{ flexShrink: 0 }}>
            <FiPrinter /> Get Print out
          </button>
        )}
        {(user?.role === 'admin' || user?.role === 'finance' || user?.role === 'cashier') && (
          <button className="btn btn-primary" onClick={() => setShowLencoCharge(true)} style={{ flexShrink: 0, background: '#4f46e5' }} title="Send a mobile-money prompt to this member's phone">
            <FiZap /> Charge via Lenco
          </button>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
              {member.first_name?.[0]}{member.last_name?.[0]}
            </div>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>{fullName}</h1>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className={`pill ${statusPill[member.membership_status] || 'pill-gray'}`}>{statusLabel[member.membership_status] || member.membership_status}</span>
                {member.card_number && <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#4f46e5', fontWeight: 700, background: '#eef2ff', padding: '2px 8px', borderRadius: 6 }}>{member.card_number}</span>}
                {member.head && (
                  <span
                    onClick={() => navigate(`/members/profile/${member.head.id}`)}
                    style={{ fontSize: 12, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                    title="Open the head member's profile"
                  >
                    Sub-member of {member.head.first_name} {member.head.last_name} ({member.head.card_number})
                  </span>
                )}
                {member.spiritual_father_name && <span style={{ fontSize: 12, color: '#6b7280' }}>Under: <strong>{member.spiritual_father_name}</strong></span>}
              </div>
            </div>
          </div>
        </div>

        {/* Quick stats — drop Dependents + Contributions for sub-members
            (no sub-roster; contributions live on the head's profile). */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            !isSubMember && { label: t('members.dependents'), value: member.dependents?.length || 0, color: '#7c3aed' },
            !isSubMember && { label: t('followup.title'), value: member.followups?.length || 0, color: '#16a34a' },
            // v1.6.1 — independently-paying sub-members get their own stat card too
            // v1.9.46 — split by currency instead of the meaningless lump sum.
            // Shows one line per currency; falls back to raw total for very old rows.
            (!isSubMember || member.pays_independently) && {
              label: t('subscriptions.title'),
              value: Object.keys(totalsByCurrency).length
                ? Object.entries(totalsByCurrency).map(([sym, amt]) => `${sym}${fmtMoney(amt)}`).join(' · ')
                : fmtMoney(totalContributions),
              color: '#2563eb',
            },
            { label: t('nav.attendance'), value: member.attendance?.length || 0, color: '#d97706' },
          ].filter(Boolean).map((s, i) => (
            <div key={i} style={{ textAlign: 'center', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 16px', minWidth: 80 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-members only get personal tabs: Profile, Attendance, Status.
          Tezkar / Follow-ups / Dependents / Contributions all live on the head's profile. */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 20, gap: 0, overflowX: 'auto' }}>
        {getTabs(t).filter(x => {
          if (!isSubMember) return true;
          // v1.6.1 — sub-members who pay independently need their own
          // Contributions tab so admins can assign subscriptions on their
          // card (not the head's).
          const baseAllowed = ['overview', 'attendance', 'status_history'];
          if (member.pays_independently) baseAllowed.push('contributions');
          return baseAllowed.includes(x.key);
        }).map(tabItem => (
          <button key={tabItem.key} onClick={() => setTab(tabItem.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            fontWeight: 600, fontSize: 13,
            color: tab === tabItem.key ? '#4f46e5' : '#6b7280',
            borderBottom: tab === tabItem.key ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {tabItem.icon} {tabItem.label}
            {tabItem.key === 'dependents' && member.dependents?.length > 0 && <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.dependents.length}</span>}
            {tabItem.key === 'followups' && member.followups?.length > 0 && <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.followups.length}</span>}
            {tabItem.key === 'contributions' && member.contributions?.length > 0 && <span style={{ background: '#dbeafe', color: '#2563eb', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.contributions.length}</span>}
            {tabItem.key === 'attendance' && member.attendance?.length > 0 && <span style={{ background: '#fef9c3', color: '#a16207', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{member.attendance.length}</span>}
          </button>
        ))}
      </div>

      {/* v1.8.8 — Outstanding-debt (legacy) card removed from admin profile
          per user request. The component + backend still exist in case we
          need to bring it back; just unmounted from this page. */}

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          <div className="card">
            <div className="card-header">{t('members.personalInfo')}</div>
            <div className="card-body">
              <Field label={t('members.fullName')} value={fullName} />
              <Field label={t('members.christianName')} value={member.christian_name} />
              <Field label={t('members.gender')} value={member.gender ? (member.gender === 'male' ? t('members.male') : t('members.female')) : null} />
              <Field label={t('members.yearOfBirth')} value={member.birth_year ? `${member.birth_year} (${t('members.age')} ${age})` : null} />
              <Field label={t('members.maritalStatus')} value={member.marital_status} />
              <Field label={t('members.occupation')} value={member.occupation} />
              <Field label="Nationality" value={member.nationality} />
              <Field label={t('common.phone')} value={(member.phone || member.whatsapp) ? <PhoneLink phone={member.phone} whatsapp={member.whatsapp} /> : null} />
              <Field label="WhatsApp" value={member.whatsapp || null} />
              <Field label={t('common.email')} value={member.email} />
              <Field label={t('common.address')} value={member.address} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header">{t('members.churchInfo')}</div>
              <div className="card-body">
                <Field label={t('members.membershipCard')} value={member.card_number} />
                <Field label={t('common.status')} value={t(`status.${member.membership_status}`) !== `status.${member.membership_status}` ? t(`status.${member.membership_status}`) : member.membership_status} />
                <Field label={t('members.spiritualFather')} value={member.spiritual_father_name} />
                <Field label={t('members.holyCommunion')} value={member.holy_communion ? t(`members.${member.holy_communion === 'taking' ? 'taking' : member.holy_communion === 'not_taking' ? 'notTaking' : 'excommunicated'}`) : null} />
                {['priest', 'deacon'].includes(String(member.title || '').toLowerCase()) && (
                  <>
                    <Field label="Date of Ordination" value={member.ordination_date} />
                    <Field label="Ordained By" value={member.ordained_by} />
                  </>
                )}
                <Field label={t('members.registeredOn')} value={member.created_at?.slice(0, 10)} />

                {/* v1.6.1 — 'Pays for themselves' checkbox. Only shown for
                    sub-members, only editable by admin. Family head has no
                    switch (they always pay against their own card). */}
                {isSubMember && user?.role === 'admin' && (
                  <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!member.pays_independently}
                        onChange={async (e) => {
                          const v = e.target.checked;
                          try {
                            await setPaysIndependently(member.id, v);
                            setMember(m => ({ ...m, pays_independently: v ? 1 : 0 }));
                            showToast(v ? 'This sub-member now pays independently' : 'This sub-member now pays through the family head');
                          } catch (err) {
                            showToast(err.response?.data?.error || 'Failed', 'error');
                          }
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 700, color: '#166534', fontSize: 13 }}>
                          Pays for themselves
                        </div>
                        <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
                          When on, this sub-member's payments, subscriptions and outstanding balance are tracked on their own card ({member.card_number}). When off (default), everything rolls up to the head {member.head?.first_name ? `(${member.head.first_name} ${member.head.last_name})` : ''}. Attendance + profile always stay personal.
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>
            {member.notes && (
              <div className="card">
                <div className="card-header">Notes</div>
                <div className="card-body" style={{ fontStyle: 'italic', color: '#374151', fontSize: 13 }}>{member.notes}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── DEPENDENTS ── */}
      {tab === 'dependents' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{member.dependents?.length || 0} dependents registered</strong>
            <button className="btn btn-secondary" onClick={() => setAddingDep(!addingDep)}><FiPlus /> Add Dependent</button>
          </div>

          {addingDep && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">New Dependent</div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="form-group"><label>Relationship</label>
                    <select value={newDep.relationship} onChange={d => { const rel = d.target.value; setNewDep(p => ({ ...p, relationship: rel, middle_name: (rel === 'son' || rel === 'daughter') ? member.first_name : '', last_name: (rel === 'son' || rel === 'daughter') ? member.middle_name || '' : '' })); }}>
                      {member.gender !== 'female' && <option value="wife">Wife</option>}
                      {member.gender !== 'male'   && <option value="husband">Husband</option>}
                      <option value="son">Son</option>
                      <option value="daughter">Daughter</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Birth Year {newDep.birth_year && <span style={{ color: '#4f46e5', fontWeight: 400 }}>→ Age {currentYear - parseInt(newDep.birth_year)}</span>}</label>
                    <input type="number" value={newDep.birth_year} onChange={setD('birth_year')} placeholder={currentYear - 10} min="1900" max={currentYear} />
                  </div>
                  <div className="form-group"><label>First Name <span className="required">*</span></label><input value={newDep.first_name} onChange={setD('first_name')} /></div>
                  <div className="form-group"><label>Middle Name</label><input value={newDep.middle_name} onChange={setD('middle_name')} /></div>
                  <div className="form-group"><label>Last Name</label><input value={newDep.last_name} onChange={setD('last_name')} /></div>
                  <div className="form-group"><label>Holy Communion</label>
                    <select value={newDep.holy_communion} onChange={setD('holy_communion')}>
                      <option value="unknown">— Select —</option>
                      <option value="taking">Taking Communion</option>
                      <option value="not_taking">Not Taking</option>
                      <option value="excommunicated">Excommunicated</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      Where they live
                      {newDep.country && newDep.country !== 'Zambia' && (
                        <span style={{ fontWeight: 400, color: '#b91c1c', marginLeft: 6, fontSize: 11 }}>
                          (excluded from demographics)
                        </span>
                      )}
                    </label>
                    <select value={newDep.country === 'Zambia' || !newDep.country ? 'Zambia' : 'Other'} onChange={(e) => {
                      const v = e.target.value;
                      setNewDep(p => ({ ...p, country: v === 'Zambia' ? 'Zambia' : '' }));
                    }}>
                      <option value="Zambia">Zambia</option>
                      <option value="Other">Other (abroad)</option>
                    </select>
                    {newDep.country !== 'Zambia' && (
                      <input
                        style={{ marginTop: 6 }}
                        value={newDep.country || ''}
                        onChange={setD('country')}
                        placeholder="Country name (e.g. UK, USA)"
                      />
                    )}
                  </div>
                  {/* v1.5.1 — phone/whatsapp for the sub-member. Backend carries
                      these through to the promoted members row on save. */}
                  <div className="form-group">
                    <label>Phone</label>
                    <PhoneInput value={newDep.phone} onChange={(v) => setNewDep(p => ({ ...p, phone: v }))} />
                  </div>
                  <div className="form-group">
                    <label>WhatsApp</label>
                    <PhoneInput value={newDep.whatsapp} onChange={(v) => setNewDep(p => ({ ...p, whatsapp: v }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary" onClick={handleAddDep} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                  <button className="btn btn-secondary" onClick={() => setAddingDep(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {(member.subMembers || []).length > 0 && (
            <div className="card" style={{ marginBottom: 16, border: '1px solid #c7d2fe', background: '#eef2ff' }}>
              <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3730a3' }}>
                <FiUserPlus /> Sub-members ({member.subMembers.length})
              </div>
              <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {member.subMembers.map(s => (
                  <div
                    key={s.id}
                    onClick={() => navigate(`/members/profile/${s.id}`)}
                    style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, minWidth: 200 }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827' }}>{[s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ')}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>{s.card_number}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(member.dependents || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiUsers /></div><p>No dependents registered</p></div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {member.dependents.map(d => (
                <div key={d.id} style={{ position: 'relative' }}>
                  <DependentCard d={d} onChangeStatus={canManage ? openDepStatusModal : undefined} />
                  {d.promoted_to_member_id ? (
                    <button
                      onClick={() => navigate(`/members/profile/${d.promoted_to_member_id}`)}
                      style={{ position: 'absolute', top: 8, right: 8, background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      title="Open this sub-member's profile"
                    >
                      <FiArrowUpRight size={12} /> Sub-member
                    </button>
                  ) : (
                    <>
                      {user?.role === 'admin' && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handlePromoteDep(d)}
                          style={{ position: 'absolute', top: 8, right: 40, fontSize: 11 }}
                          title="Give this dependent their own card number, QR, and login"
                        >
                          <FiUserPlus /> Promote
                        </button>
                      )}
                      {canManage && <button className="btn-icon danger" onClick={() => handleDeleteDep(d.id)} style={{ position: 'absolute', top: 8, right: 8 }}><FiTrash2 /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TEZKAR ── */}
      {tab === 'tezkar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{tezkarList.length} name{tezkarList.length !== 1 ? 's' : ''} registered for memorial prayer</strong>
            <button className="btn btn-secondary" onClick={() => setAddingTezkar(!addingTezkar)}><FiPlus /> Add Name</button>
          </div>

          {addingTezkar && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    value={newTezkarName}
                    onChange={e => setNewTezkarName(e.target.value)}
                    placeholder="Full name of the deceased..."
                    style={{ flex: 1 }}
                    autoFocus
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && newTezkarName.trim()) {
                        await addTezkar(id, { name: newTezkarName.trim() });
                        setNewTezkarName('');
                        setAddingTezkar(false);
                        loadTezkar();
                      }
                    }}
                  />
                  <button className="btn btn-primary" onClick={async () => {
                    if (!newTezkarName.trim()) return;
                    await addTezkar(id, { name: newTezkarName.trim() });
                    setNewTezkarName('');
                    setAddingTezkar(false);
                    loadTezkar();
                  }}>Save</button>
                  <button className="btn btn-secondary" onClick={() => { setAddingTezkar(false); setNewTezkarName(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {tezkarList.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiBookOpen /></div><p>No names registered yet</p></div>
          ) : (() => {
            const ordinal = (n) => {
              const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
              return n + (s[(v - 20) % 10] || s[v] || s[0]);
            };
            const sorted = (() => {
              const list = [...tezkarList];
              if (tezkarSort === 'az') return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              if (tezkarSort === 'newest') return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
              if (tezkarSort === 'oldest') return list.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
              return list; // custom — server order (sort_order)
            })();
            const move = async (idx, dir) => {
              const next = idx + dir;
              if (next < 0 || next >= sorted.length) return;
              const ids = sorted.map(t => t.id);
              [ids[idx], ids[next]] = [ids[next], ids[idx]];
              // optimistic
              setTezkarList(ids.map(tid => tezkarList.find(t => t.id === tid)).filter(Boolean));
              try { await reorderTezkar(id, ids); } catch (e) { showToast('Failed to reorder', 'error'); }
              loadTezkar();
            };
            return (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f3f4f6', background: '#fafafa', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sort by</span>
                  <select
                    value={tezkarSort}
                    onChange={e => setTezkarSort(e.target.value)}
                    style={{ flex: 1, minWidth: 160, fontSize: 13, fontWeight: 600, color: '#111827', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px' }}
                  >
                    <option value="custom">Custom (arrange manually)</option>
                    <option value="az">Alphabetical (A → Z)</option>
                    <option value="newest">Recently added</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  {sorted.map((t, i) => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < sorted.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <span style={{
                        minWidth: 38, height: 34, display: 'grid', placeItems: 'center', borderRadius: 8,
                        background: i === 0 ? 'linear-gradient(135deg,#fde68a,#f59e0b)' : '#eef2ff',
                        color: i === 0 ? '#78350f' : '#4f46e5',
                        fontSize: 11, fontWeight: 800, letterSpacing: '0.02em', textTransform: 'uppercase',
                      }}>{ordinal(i + 1)}</span>
                      <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#111827' }}>{t.name}</span>
                      {tezkarSort === 'custom' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button
                            onClick={() => move(i, -1)}
                            disabled={i === 0}
                            title="Move up"
                            style={{ width: 26, height: 20, display: 'grid', placeItems: 'center', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: i === 0 ? '#d1d5db' : '#4f46e5', cursor: i === 0 ? 'not-allowed' : 'pointer', padding: 0 }}
                          >▲</button>
                          <button
                            onClick={() => move(i, 1)}
                            disabled={i === sorted.length - 1}
                            title="Move down"
                            style={{ width: 26, height: 20, display: 'grid', placeItems: 'center', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: i === sorted.length - 1 ? '#d1d5db' : '#4f46e5', cursor: i === sorted.length - 1 ? 'not-allowed' : 'pointer', padding: 0 }}
                          >▼</button>
                        </div>
                      )}
                      <button className="btn-icon danger" onClick={async () => {
                        if (!window.confirm(`Remove "${t.name}" from memorial list?`)) return;
                        await deleteTezkar(t.id);
                        loadTezkar();
                      }}><FiTrash2 /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── FOLLOW-UPS ── */}
      {tab === 'followups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{member.followups?.length || 0} follow-up visits recorded</strong>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Recorded via QR scan</span>
          </div>

          {(member.followups || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiHeart /></div><p>No follow-ups recorded yet</p></div>
          ) : (
            <div className="table-card">
              <table>
                <thead><tr><th>#</th><th>Date</th><th>Status</th><th>Next Follow-up</th><th>Notes</th><th>Done By</th><th></th></tr></thead>
                <tbody>
                  {member.followups.map((f, i) => (
                    <tr key={f.id}>
                      <td>{i + 1}</td>
                      <td>{f.date}</td>
                      <td>{f.status && <span className={`pill ${f.status === 'good' ? 'pill-green' : f.status === 'critical' ? 'pill-red' : f.status === 'needs_attention' ? 'pill-yellow' : 'pill-gray'}`}>{f.status.replace('_', ' ')}</span>}</td>
                      <td>{f.next_followup_date || '—'}</td>
                      <td style={{ maxWidth: 200, color: '#374151', fontStyle: 'italic' }}>{f.notes || '—'}</td>
                      <td>{f.done_by_name || '—'}</td>
                      <td><button className="btn-icon danger" onClick={() => handleDeleteFup(f.id)}><FiTrash2 /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── CONTRIBUTIONS ── */}
      {tab === 'contributions' && (
        <div>
          {/* Active subscriptions for this member — admin sees this before
              the lifetime CR list so they don't accidentally double-register. */}
          {(member.subscriptions || []).length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Current Subscriptions ({member.subscriptions.length})</span>
                {user?.role === 'admin' && (
                  <button className="btn btn-primary btn-sm" onClick={openAssignSub}>
                    <FiPlus /> Assign Subscription
                  </button>
                )}
              </div>
              {/* v1.9.64 — wrap in horizontal-scroll container so the 6-col
                  subscriptions table doesn't clip off the right edge on
                  narrow phones. Table itself keeps its natural min-width. */}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ minWidth: 640 }}>
                <thead>
                  <tr>
                    <th>Type</th><th>Frequency</th><th>Pledge</th><th>Start date</th><th>Unpaid / Overdue</th>
                    {user?.role === 'admin' && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {member.subscriptions.map(s => {
                    const pledges = (() => { try { return JSON.parse(s.pledges_json || '{}'); } catch { return {}; } })();
                    const fallback = {};
                    const currencies = (churchProfile.currencies && churchProfile.currencies.length) ? churchProfile.currencies : ['$', 'K'];
                    if (Object.keys(pledges).length === 0) {
                      if (parseFloat(s.intended_amount_usd) > 0 && currencies[0]) fallback[currencies[0]] = parseFloat(s.intended_amount_usd);
                      if (parseFloat(s.intended_amount_kwacha) > 0 && currencies[1]) fallback[currencies[1]] = parseFloat(s.intended_amount_kwacha);
                    }
                    const display = Object.keys(pledges).length ? pledges : fallback;
                    return (
                      <tr key={s.id}>
                        <td><strong>{s.type_name}</strong></td>
                        <td style={{ textTransform: 'capitalize' }}>{s.frequency}</td>
                        <td>
                          {Object.entries(display).filter(([, v]) => parseFloat(v) > 0).map(([sym, v]) => (
                            <div key={sym} style={{ fontSize: 13 }}>{sym}{parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                          ))}
                          {Object.keys(display).length === 0 && '—'}
                        </td>
                        <td>{s.start_date || '—'}</td>
                        <td>
                          {s.overdue_count > 0
                            ? <span className="pill pill-red">{s.overdue_count} overdue</span>
                            : s.unpaid_count > 0
                              ? <span className="pill pill-yellow">{s.unpaid_count} due</span>
                              : <span className="pill pill-green">on time</span>}
                          {/* v1.9.6 — owed amounts per currency so admin can
                              see at a glance what this member owes on THIS
                              subscription. Only render when non-zero. */}
                          {(() => {
                            const usd = parseFloat(s.total_owed_usd || 0);
                            const k   = parseFloat(s.total_owed_kwacha || 0);
                            if (usd <= 0 && k <= 0) return null;
                            const parts = [];
                            // v1.9.51 — cap to 2 decimals so floating-point
                            // remainders like K549.997 render as K550.00.
                            if (usd > 0) parts.push(`$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                            if (k   > 0) parts.push(`K${k.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                            const overdue = s.overdue_count > 0;
                            return (
                              <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4, color: overdue ? '#b91c1c' : '#b45309' }}>
                                Owed: {parts.join(' + ')}
                              </div>
                            );
                          })()}
                        </td>
                        {/* v1.6.3 — admin-only unsubscribe. Members can no
                            longer cancel their own pledges from the portal
                            (backend enforces role='admin' too). */}
                        {user?.role === 'admin' && (
                          <td>
                            <button
                              className="btn-icon danger"
                              title="Unsubscribe this member from this contribution type"
                              onClick={async () => {
                                if (!window.confirm(`Unsubscribe ${member.first_name || 'this member'} from "${s.type_name}"?`)) return;
                                try {
                                  await unsubscribeFromType(s.fund_category_id, member.id);
                                  showToast('Unsubscribed');
                                  load();
                                } catch (e) {
                                  showToast(e.response?.data?.error || 'Failed', 'error');
                                }
                              }}
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
          {(member.subscriptions || []).length === 0 && user?.role === 'admin' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={openAssignSub}>
                <FiPlus /> Assign Subscription
              </button>
            </div>
          )}
          <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
            <div className="card" style={{ flex: 1, padding: '14px 20px' }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Total Contributions</div>
              {/* v1.9.46 — split by currency ($, K) instead of a currency-blind
                  sum that added $70 to K1,900 as "1,970" (nonsense). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(totalsByCurrency).map(([sym, amt]) => (
                  <div key={sym || 'raw'} style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>
                    {sym}{fmtMoney(amt)}
                  </div>
                ))}
                {Object.keys(totalsByCurrency).length === 0 && (
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#9ca3af' }}>—</div>
                )}
              </div>
            </div>
            <div className="card" style={{ flex: 1, padding: '14px 20px' }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Number of Transactions</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed' }}>{member.contributions?.length || 0}</div>
            </div>
          </div>

          {(member.contributions || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiDollarSign /></div><p>No contributions recorded yet</p></div>
          ) : (
            <div className="table-card">
              <table>
                <thead><tr><th>#</th><th>Date</th><th>CR No.</th><th>Fund</th><th>Method</th><th>Amount</th></tr></thead>
                <tbody>
                  {member.contributions.map((c, i) => (
                    <tr key={c.id}>
                      <td>{i + 1}</td>
                      <td>{c.date}</td>
                      <td>
                        <Link to={`/finance/cr/${c.cr_number}`} className="pill pill-gray" style={{ fontFamily: 'monospace', fontSize: 11, textDecoration: 'none' }} title="View this receipt">
                          {c.cr_number}
                        </Link>
                      </td>
                      <td>{c.fund_name || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{c.payment_method}</td>
                      <td>
                        {/* v1.9.51 — show currency prefix on each row. Split
                            $/K so a mixed-currency CR shows both. Falls back
                            to raw amount for pre-multi-currency rows. */}
                        <strong style={{ color: '#16a34a' }}>
                          {(() => {
                            const usd = parseFloat(c.amount_usd || 0) || 0;
                            const kwa = parseFloat(c.amount_kwacha || 0) || 0;
                            const parts = [];
                            if (usd > 0) parts.push(`$${fmtMoney(usd)}`);
                            if (kwa > 0) parts.push(`K${fmtMoney(kwa)}`);
                            return parts.length ? parts.join(' + ') : fmtMoney(c.amount);
                          })()}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f9fafb' }}>
                    <td colSpan={5} style={{ padding: '10px 16px', fontWeight: 700 }}>Total</td>
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: '#16a34a' }}>
                      {/* v1.9.46 — one line per currency, no lumping */}
                      {Object.entries(totalsByCurrency).map(([sym, amt]) => (
                        <div key={sym || 'raw'}>{sym}{fmtMoney(amt)}</div>
                      ))}
                      {Object.keys(totalsByCurrency).length === 0 && '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ATTENDANCE ── */}
      {tab === 'attendance' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>{member.attendance?.length || 0} services attended</strong>
          </div>
          {(member.attendance || []).length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><FiActivity /></div><p>No attendance records found</p></div>
          ) : (
            <div className="table-card">
              <table>
                <thead><tr><th>#</th><th>Date</th><th>Service</th><th>Type</th></tr></thead>
                <tbody>
                  {member.attendance.map((a, i) => (
                    <tr key={a.id}>
                      <td>{i + 1}</td>
                      <td>{a.date}</td>
                      <td>{a.service_name}</td>
                      <td><span className="pill pill-purple" style={{ textTransform: 'capitalize' }}>{a.type}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── STATUS HISTORY ── */}
      {tab === 'status_history' && (
        <div>
          <strong style={{ fontSize: 14, display: 'block', marginBottom: 16 }}>{member.statusHistory?.length || 0} status changes recorded</strong>

          {/* Registration as first event */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#4f46e5', flexShrink: 0 }} />
              <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />
            </div>
            <div style={{ paddingBottom: 20, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Member Registered</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{member.created_at?.slice(0, 16)} · System</div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 3 }}>Card: <strong>{member.card_number || '—'}</strong></div>
                </div>
              </div>
            </div>
          </div>

          {(member.statusHistory || []).length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, marginLeft: 36 }}>No status changes recorded after registration</div>
          ) : [...member.statusHistory].reverse().map((h, i, arr) => (
            <div key={h.id} style={{ display: 'flex', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#7c3aed', flexShrink: 0 }} />
                {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />}
              </div>
              <div style={{ paddingBottom: 24, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  {h.old_status && <span className={`pill ${statusPill[h.old_status] || 'pill-gray'}`} style={{ fontSize: 12 }}>{statusLabel[h.old_status] || h.old_status}</span>}
                  {h.old_status && <span style={{ color: '#9ca3af', fontWeight: 700 }}>→</span>}
                  <span className={`pill ${statusPill[h.new_status] || 'pill-gray'}`} style={{ fontSize: 12 }}>{statusLabel[h.new_status] || h.new_status}</span>
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>{h.changed_at?.slice(0, 16)} · {h.changed_by_name}</div>
                {(h.reason || h.custom_reason) && <div style={{ fontSize: 13, color: '#374151' }}>Reason: {h.custom_reason || h.reason}</div>}
                {h.notes && <div style={{ fontSize: 13, color: '#374151', marginTop: 2, fontStyle: 'italic' }}>{h.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dependent Status Change Modal */}
      {depStatusModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div>
                <h3>Change Dependent Status</h3>
                <p>
                  {(depStatusModal.first_name ? `${depStatusModal.first_name} ${depStatusModal.middle_name || ''} ${depStatusModal.last_name || ''}`.trim() : depStatusModal.name)}
                  {' · Current: '}
                  <span className={`pill ${DEP_STATUS_PILL[depStatusModal.life_status || 'active']}`} style={{ fontSize: 11 }}>
                    {DEP_STATUS_LABEL[depStatusModal.life_status || 'active']}
                  </span>
                </p>
              </div>
              <button className="btn-icon" onClick={() => setDepStatusModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Status <span className="required">*</span></label>
                <select value={depStatusForm.life_status} onChange={e => setDepStatusForm(p => ({ ...p, life_status: e.target.value, reason: '', custom_reason: '' }))}>
                  {!DEP_STATUS_OPTIONS.some(s => s.value === depStatusForm.life_status) && depStatusForm.life_status && (
                    <option value={depStatusForm.life_status}>{DEP_STATUS_LABEL[depStatusForm.life_status] || depStatusForm.life_status}</option>
                  )}
                  {DEP_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {depStatusForm.life_status && (
                <div className="form-group">
                  <label>Reason</label>
                  <select value={depStatusForm.reason} onChange={e => setDepStatusForm(p => ({ ...p, reason: e.target.value, custom_reason: '' }))}>
                    <option value="">— Select reason —</option>
                    {(DEP_REASONS[depStatusForm.life_status] || DEP_REASONS.other).map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              )}

              {(depStatusForm.reason === 'Other' || depStatusForm.life_status === 'other') && (
                <div className="form-group">
                  <label>Specify Reason <span className="required">*</span></label>
                  <input value={depStatusForm.custom_reason} onChange={e => setDepStatusForm(p => ({ ...p, custom_reason: e.target.value }))} placeholder="Write the reason" />
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea value={depStatusForm.notes} onChange={e => setDepStatusForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Additional notes (optional)" />
              </div>

              {depHistory.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>History</div>
                  {depHistory.map(h => (
                    <div key={h.id} style={{ fontSize: 12, color: '#374151', marginBottom: 8, paddingLeft: 10, borderLeft: '2px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {h.old_status && <span className={`pill ${DEP_STATUS_PILL[h.old_status] || 'pill-gray'}`} style={{ fontSize: 10 }}>{DEP_STATUS_LABEL[h.old_status] || h.old_status}</span>}
                        {h.old_status && <span style={{ color: '#9ca3af', fontWeight: 700 }}>→</span>}
                        <span className={`pill ${DEP_STATUS_PILL[h.new_status] || 'pill-gray'}`} style={{ fontSize: 10 }}>{DEP_STATUS_LABEL[h.new_status] || h.new_status}</span>
                        <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>{h.changed_at?.slice(0, 10)}</span>
                      </div>
                      {(h.reason || h.custom_reason) && <div style={{ marginTop: 2 }}>Reason: {h.custom_reason || h.reason}</div>}
                      {h.notes && <div style={{ marginTop: 2, fontStyle: 'italic', color: '#6b7280' }}>{h.notes}</div>}
                      {h.changed_by_name && <div style={{ marginTop: 2, color: '#9ca3af', fontSize: 11 }}>by {h.changed_by_name}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDepStatusModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveDepStatus} disabled={saving}>
                {saving ? t('common.saving') : 'Confirm Status Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {assignSub && (() => {
        const type = funds.find(f => String(f.id) === String(assignSub.fund_category_id));
        const currencies = (churchProfile.currencies && churchProfile.currencies.length) ? churchProfile.currencies : ['$', 'K'];
        // Don't offer types the member already has an active subscription for —
        // the backend rejects duplicates anyway, but filtering here makes the
        // UX honest. Show a "Already subscribed to X" line if there's nothing left.
        const subscribedIds = new Set((member.subscriptions || []).map(s => String(s.fund_category_id)));
        const availableFunds = funds.filter(f => !subscribedIds.has(String(f.id)));
        // Live preview of how many periods will be generated by the back-fill.
        let periodsPreview = 0;
        if (type?.frequency && assignSub.start_date) {
          const start = new Date(assignSub.start_date + 'T12:00:00');
          const today = new Date();
          if (!isNaN(start) && start <= today) {
            if (type.frequency === 'monthly') periodsPreview = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth()) + 1;
            else if (type.frequency === 'weekly') periodsPreview = Math.floor((today - start) / (7 * 86400000)) + 1;
            else if (type.frequency === 'yearly') periodsPreview = today.getFullYear() - start.getFullYear() + 1;
            if (periodsPreview < 1) periodsPreview = 1;
            if (periodsPreview > 200) periodsPreview = 200;
          }
        }
        return (
          <div className="modal-overlay" onClick={() => !assignSub.saving && setAssignSub(null)}>
            <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div><h3>Assign Subscription</h3><p>Back-date to make historical arrears visible.</p></div>
                <button className="btn-icon" onClick={() => setAssignSub(null)}><FiX /></button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group form-full">
                    <label>Contribution type</label>
                    <select value={assignSub.fund_category_id}
                      onChange={e => setAssignSub(p => ({ ...p, fund_category_id: e.target.value }))}>
                      <option value="">—</option>
                      {availableFunds.map(f => <option key={f.id} value={f.id}>{f.name} ({f.frequency})</option>)}
                    </select>
                    {availableFunds.length === 0 && (
                      <small style={{ color: '#a16207', fontSize: 11, marginTop: 4, display: 'block' }}>
                        This member is already subscribed to every available type.
                      </small>
                    )}
                  </div>
                  {currencies.map(sym => (
                    <div className="form-group" key={sym}>
                      <label>Pledge {sym}</label>
                      <input type="number" step="0.01" min={0} placeholder="0.00"
                        value={assignSub.amounts[sym] || ''}
                        onChange={e => setAssignSub(p => ({ ...p, amounts: { ...p.amounts, [sym]: e.target.value } }))} />
                    </div>
                  ))}
                  <div className="form-group form-full">
                    <label>Start date <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>(past = back-fill arrears; future = member already paid up to that date)</span></label>
                    <input type="date" value={assignSub.start_date}
                      onChange={e => setAssignSub(p => ({ ...p, start_date: e.target.value }))} />
                  </div>
                </div>
                {type?.frequency && periodsPreview > 0 && (
                  <div style={{ marginTop: 12, padding: '10px 14px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 13, color: '#3730a3' }}>
                    Will generate <strong>{periodsPreview}</strong> {type.frequency.replace(/ly$/, '')}{periodsPreview === 1 ? '' : 's'} of unpaid periods.
                    {periodsPreview >= 200 && <span style={{ color: '#dc2626' }}> (capped at 200)</span>}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setAssignSub(null)} disabled={assignSub.saving}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmitAssignSub} disabled={assignSub.saving}>
                  {assignSub.saving ? 'Saving…' : 'Subscribe'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showLencoCharge && (
        <AdminLencoChargeModal
          member={member}
          onClose={() => setShowLencoCharge(false)}
          onSuccess={() => { setToast({ msg: 'Payment received — refreshing…', type: 'success' }); load(); }}
        />
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default MemberProfile;
